import { StoreActionApi } from 'react-sweet-state';

import {
  getProgressForLessons,
  LearningProgressStatus,
  ProgressForLessons,
  saveProgressForLesson,
} from 'src/services/http/progress';
import { ProgressState } from 'src/store/progress/progressStore';
import { User } from 'src/types';
import { ContentfulSmCourse } from 'src/graphql/queries/GetSmCourse';
import { ContentfulSmLesson } from 'src/graphql/queries/GetSmLesson';
import { formatToISODate } from 'src/utils/date';
import { completeSectionAsync, updateLessonProgressUponLessonStart } from 'src/store/progress/utils/progressUtils';

export const fetchProgressForLessons =
  (lessonId: string, course: ContentfulSmCourse | undefined, user: User | null | undefined) =>
    async ({ setState }: StoreActionApi<ProgressState>): Promise<void> => {
      if (!user) {
        return;
      }

      const lessonIds = course?.lessonsCollection.items.map((l) => l.sys.id) || [lessonId];

      if (!lessonIds.length) {
        return;
      }

      setState({
        progressForLessons: await getProgressForLessons(lessonIds),
      });
    };

export const completeSection =
  (section: string, lesson: ContentfulSmLesson, preview: boolean, user: User | null | undefined) =>
    async ({ setState, getState }: StoreActionApi<ProgressState>): Promise<void> => {
      const { progressForLessons } = getState();

      if (!progressForLessons) {
        return;
      }

      const currentLessonProgress = progressForLessons?.lessons.find(
        (p: ProgressForLessons['lessons'][0]) => p.lessonId === lesson.sys.id,
      );
      const currentSectionProgress = currentLessonProgress?.section.find(
        (s: { sectionId: string; status: LearningProgressStatus }) => s.sectionId === section,
      );
      const isAlreadyTracked = currentSectionProgress?.status === LearningProgressStatus.COMPLETED;

      if (isAlreadyTracked) {
        return;
      }

      setState({ progressForLessons: completeSectionAsync(progressForLessons, section, lesson, user, preview) });
    };

export const startLesson =
  (lesson: ContentfulSmLesson, user: User | null | undefined) =>
    async ({ getState, setState }: StoreActionApi<ProgressState>): Promise<void> => {
      const { progressForLessons } = getState();

      const lessonProgress = progressForLessons?.lessons.find((p) => p.lessonId === lesson.sys.id);
      const isNewLessonForUser = !lessonProgress || lessonProgress.status === LearningProgressStatus.NOT_STARTED;

      if (user && progressForLessons && isNewLessonForUser) {
        await saveProgressForLesson({
          atlassianId: user.account_id,
          lesson: {
            lessonId: lesson.sys.id,
            status: LearningProgressStatus.IN_PROGRESS,
            startedDate: formatToISODate(new Date(Date.now())),
          },
        });

        setState({
          progressForLessons: updateLessonProgressUponLessonStart(progressForLessons, lesson.sys.id),
        });
      }
    };
